/**
 * Phase 2 lazy-load for the header menu drawer.
 *
 * The in-page drawer ships only the top-level items + empty `<ul data-submenu>` placeholders
 * (see blocks/_header-menu-multi-level.liquid). The full level-2/3 submenus (~2,700 els) are kept
 * out of initial HTML and rendered by an off-page fragment (sections/menu-drawer-fragment.liquid via
 * templates/collection.menudrawer.liquid, `{% layout none %}`). This controller fetches that fragment
 * ONCE — prefetched on idle, and guaranteed on first interaction with a menu toggle — and replaces
 * each placeholder with its real childlist, matched on `data-submenu`.
 */

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const FRAGMENT_URL = `${root}collections/all?view=menudrawer`;

/** @type {Promise<string|null>|null} */
let fetchPromise = null;
let injected = false;

function fetchFragment() {
  if (fetchPromise) return fetchPromise;
  fetchPromise = fetch(FRAGMENT_URL, { headers: { 'X-Requested-With': 'fetch' } })
    .then((response) => {
      if (!response.ok) throw new Error(`menu fragment HTTP ${response.status}`);
      return response.text();
    })
    .catch((error) => {
      console.error('[menu-drawer-lazy] fetch failed', error);
      fetchPromise = null; // allow a retry on the next interaction
      return null;
    });
  return fetchPromise;
}

/**
 * Top-level `data-submenu` placeholders that have not been filled with a childlist yet.
 * @returns {Element[]}
 */
function emptyPlaceholders() {
  return Array.from(
    document.querySelectorAll('ul.menu-drawer__menu--childlist[data-submenu]')
  ).filter((el) => el.childElementCount === 0);
}

async function hydrate() {
  if (injected) return;

  // Nothing left to fill (already hydrated, or no placeholders on this page) — latch and stop.
  if (emptyPlaceholders().length === 0) {
    injected = true;
    return;
  }

  const html = await fetchFragment();
  if (!html || injected) return;

  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const fullLists = fragment.querySelectorAll('ul.menu-drawer__menu--childlist[data-submenu]');

  let replaced = 0;
  fullLists.forEach((fullList) => {
    const handle = fullList.getAttribute('data-submenu');
    if (!handle) return;

    // A handle can appear in MORE THAN ONE placeholder (e.g. the same menu wired into separate
    // header menu blocks), so fill EVERY still-empty placeholder, not just the first match.
    const placeholders = document.querySelectorAll(
      `ul.menu-drawer__menu--childlist[data-submenu="${CSS.escape(handle)}"]`
    );

    placeholders.forEach((placeholder) => {
      // Only fill placeholders that are still empty (idempotent / safe against repeat calls).
      if (placeholder.childElementCount === 0) {
        // Each placeholder needs its own copy of the childlist (a node can't be in two places).
        placeholder.replaceWith(fullList.cloneNode(true));
        replaced += 1;
      }
    });
  });

  if (replaced > 0) {
    document.dispatchEvent(new CustomEvent('menu-drawer:hydrated', { detail: { count: replaced } }));
  }

  // Only latch as fully done once EVERY placeholder has a childlist. If the fragment matched only
  // some handles (e.g. theme menu settings out of sync with the fragment's hardcoded handle list),
  // leave `injected` false so a later toggle interaction retries the still-empty placeholders.
  if (emptyPlaceholders().length === 0) {
    injected = true;
  }
}

const TOGGLE_SELECTOR = 'summary.header__icon--summary, [on\\:click$="menu-drawer/toggle"]';

/** @param {Event} event */
function onToggleInteract(event) {
  const target = event.target;
  if (target instanceof Element && target.closest(TOGGLE_SELECTOR)) {
    hydrate();
  }
}

function schedulePrefetch() {
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
  idle(() => fetchFragment());
}

// Prefetch when the browser is idle so the content is usually ready before the drawer is opened.
if (document.readyState !== 'loading') schedulePrefetch();
else document.addEventListener('DOMContentLoaded', schedulePrefetch, { once: true });

// Guarantee hydration the moment the user shows intent to use the menu. These all fire in the
// capture phase, BEFORE the toggle's own click handler opens the drawer, so the fetch (or, on a
// warm prefetch, the injection itself) gets a head start and submenu placeholders are filled by the
// time the user expands a level-1 accordion. `pointerdown` is included so touch devices — which have
// no hover — still start hydrating on first tap rather than waiting for the click. If the fetch is
// still in flight when the drawer opens, hydrate() injects into the (already-open) placeholders the
// moment it resolves, so they self-heal instead of staying blank.
document.addEventListener('pointerover', onToggleInteract, { capture: true });
document.addEventListener('pointerdown', onToggleInteract, { capture: true });
document.addEventListener('focusin', onToggleInteract, { capture: true });
document.addEventListener('click', onToggleInteract, { capture: true });
